import { Alert, Box, CircularProgress, Link, MenuItem, Stack } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { D365TextField } from '@/components/D365FormControls';
import { RecordFormLayout } from '@/components/RecordFormLayout';
import {
  getLocationBodies,
  getServiceLocation,
  saveBodyOfWater,
  type BodyOfWater,
  type BodyOfWaterInput,
} from '@/features/service-locations/api/serviceLocations';

export function PoolPage() {
  const params = useParams();
  const clientId = Number(params.clientId);
  const locationId = Number(params.locationId);
  const isNew = params.poolId === 'new';
  const poolId = Number(params.poolId);
  const valid =
    [clientId, locationId].every((id) => Number.isInteger(id) && id > 0) &&
    (isNew || (Number.isInteger(poolId) && poolId > 0));
  const bodies = useQuery({
    queryKey: ['location', clientId, locationId, 'bodies'],
    queryFn: () => getLocationBodies(clientId, locationId),
    enabled: valid && !isNew,
  });
  const location = useQuery({
    queryKey: ['location', clientId, locationId],
    queryFn: () => getServiceLocation(clientId, locationId),
    enabled: valid,
  });
  if (!valid || bodies.isError || location.isError)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This pool could not be loaded.
      </Alert>
    );
  if (location.isPending || (!isNew && bodies.isPending))
    return <CircularProgress aria-label="Loading pool" sx={{ m: 3 }} />;
  const pool = bodies.data?.find((item) => item.id === poolId);
  if (!isNew && !pool)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This pool was not found at this service location.
      </Alert>
    );
  return (
    <PoolEditor
      key={`${locationId}-${params.poolId}`}
      clientId={clientId}
      locationId={locationId}
      locationName={location.data.name}
      pool={pool}
    />
  );
}

function PoolEditor({
  clientId,
  locationId,
  locationName,
  pool,
}: {
  clientId: number;
  locationId: number;
  locationName: string;
  pool?: BodyOfWater;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initial: BodyOfWaterInput = {
    name: pool?.name ?? '',
    width: pool?.width == null ? null : Number(pool.width) / 100,
    length: pool?.length == null ? null : Number(pool.length) / 100,
    depth: pool?.depth == null ? null : Number(pool.depth) / 100,
    volume: pool?.volume == null ? null : Number(pool.volume) / 100,
    volumeUnit: pool?.volume_unit === 'litres' ? 'litres' : 'gallons',
  };
  const [form, setForm] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const key = ['location', clientId, locationId, 'bodies'];
  const root = `/clients/${clientId}/service-locations/${locationId}`;
  const back = () => {
    if (!dirty || window.confirm('Discard unsaved pool details?'))
      void navigate(`${root}?tab=pools`);
  };
  const save = useMutation({
    mutationFn: async (close: boolean) => ({
      pool: await saveBodyOfWater(clientId, locationId, form, pool?.id),
      close,
    }),
    onSuccess: ({ pool: updated, close }) => {
      queryClient.setQueryData<BodyOfWater[]>(key, (items) => [
        ...(items ?? []).filter((item) => item.id !== updated.id),
        updated,
      ]);
      setSaved(form);
      if (close) void navigate(`${root}?tab=pools`);
      else if (!pool) void navigate(`${root}/pools/${updated.id}`, { replace: true });
    },
  });
  return (
    <>
      <RecordFormLayout
        title={pool ? form.name || 'Pool' : 'New Pool'}
        type={`Pool · ${locationName}`}
        saving={save.isPending}
        disabled={!form.name.trim()}
        dirty={dirty}
        error={save.isError}
        onBack={back}
        onSave={() => save.mutate(false)}
        onSaveAndClose={() => save.mutate(true)}
      >
        <Stack spacing={2}>
          <D365TextField
            label="Pool name"
            required
            value={form.name}
            disabled={save.isPending}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
          {(['width', 'length', 'depth', 'volume'] as const).map((field) => (
            <D365TextField
              key={field}
              label={
                field === 'volume'
                  ? 'Volume'
                  : `${field.charAt(0).toUpperCase()}${field.slice(1)} (m)`
              }
              type="number"
              value={form[field] ?? ''}
              disabled={save.isPending}
              slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
              onChange={(event) =>
                setForm({
                  ...form,
                  [field]: event.target.value === '' ? null : Number(event.target.value),
                })
              }
            />
          ))}
          <D365TextField
            select
            label="Volume unit"
            slotProps={{ select: { SelectDisplayProps: { 'aria-label': 'Volume unit' } } }}
            value={form.volumeUnit}
            disabled={save.isPending}
            onChange={(event) =>
              setForm({ ...form, volumeUnit: event.target.value as 'litres' | 'gallons' })
            }
          >
            <MenuItem value="litres">Litres</MenuItem>
            <MenuItem value="gallons">Gallons</MenuItem>
          </D365TextField>
        </Stack>
      </RecordFormLayout>
      <Box sx={{ px: 1, pb: 2 }}>
        <Alert severity="info">
          Standard tasks apply to every pool on every job. Manage the shared list in{' '}
          <Link component={RouterLink} to="/settings?tab=tasks">
            Settings → Tasks
          </Link>
          .
        </Alert>
      </Box>
    </>
  );
}
